/**
 * pacsExchange.js — чистый разбор ответов DICOMweb для двух кнопок сравнения:
 * «Прошлые с PACS» (QIDO) и «В архив» (STOW). Ни одного импорта; проверяется
 * в node на образцах ответов (tests/pacs_exchange_test.mjs). Сами запросы шлёт
 * существующий клиент (io/dicomWebClient.js) — здесь только превращение его
 * JSON в то, что можно показать врачу и на что можно опереться.
 *
 * ЗАЧЕМ ОТДЕЛЬНО. Ответ сервера — DICOM-JSON с тегами вида
 * {"00080020":{"vr":"DA","Value":["20240117"]}}; разбор таких структур —
 * ровно то место, где молчаливая опечатка в теге даёт «пустой список» без
 * единой ошибки. Поэтому разбор живёт чистым и гоняется тестом, а не сидит
 * внутри обработчика кнопки.
 */

const _знач = (з, тег) => з?.[тег]?.Value?.[0];
const _строка = (з, тег) => {
  const v = _знач(з, тег);
  if (v == null) return '';
  if (typeof v === 'object') return String(v.Alphabetic || '');
  return String(v);
};
const _число = (з, тег) => {
  const v = Number(_знач(з, тег));
  return Number.isFinite(v) ? v : 0;
};
const _список = (з, тег) => {
  const v = з?.[тег]?.Value;
  return Array.isArray(v) ? v.map(String) : [];
};

/**
 * QIDO /studies → список прошлых исследований пациента.
 *
 * @param {Array<object>} ответQIDO — как вернул сервер (массив DICOM-JSON)
 * @param {object} [o] — { patientId: сверить принадлежность (сервер обязан был
 *   отфильтровать сам, но чужого пациента в списке онкоприёма быть не должно
 *   ни при какой ошибке сервера — поэтому сверяем и здесь);
 *   исключитьUID: Set уже загруженных StudyInstanceUID }
 * @returns {{исследования: Array<{studyUID, дата, описание, пациент, patientId,
 *            модальности, кадров}>, отсеяноЧужих: number}}
 */
export function разобратьПрошлые(ответQIDO, o = {}) {
  const свои = [], patientId = o.patientId ? String(o.patientId) : null;
  const исключить = o.исключитьUID || new Set();
  let отсеяноЧужих = 0;
  for (const з of Array.isArray(ответQIDO) ? ответQIDO : []) {
    const uid = _строка(з, '0020000D');
    if (!uid) continue;
    const pid = _строка(з, '00100020');
    if (patientId && pid && pid !== patientId) { отсеяноЧужих++; continue; }
    if (исключить.has(uid)) continue;
    свои.push({
      studyUID: uid,
      дата: _строка(з, '00080020').replace(/\D/g, '').slice(0, 8),
      описание: _строка(з, '00081030'),
      пациент: _строка(з, '00100010'),
      patientId: pid,
      модальности: _список(з, '00080061'),
      кадров: _число(з, '00201208'),
    });
  }
  // Новые сверху: врач сравнивает с ближайшим прошлым, а не с древностью.
  свои.sort((а, б) => б.дата.localeCompare(а.дата));
  return { исследования: свои, отсеяноЧужих };
}

/** QIDO /studies/{uid}/series → серии исследования, крупные сверху. */
export function разобратьСерии(ответQIDO) {
  const серии = [];
  for (const з of Array.isArray(ответQIDO) ? ответQIDO : []) {
    const uid = _строка(з, '0020000E');
    if (!uid) continue;
    серии.push({
      seriesUID: uid,
      описание: _строка(з, '0008103E'),
      модальность: _строка(з, '00080060'),
      кадров: _число(з, '00201209'),
    });
  }
  серии.sort((а, б) => б.кадров - а.кадров);
  return серии;
}

/**
 * Ответ STOW → честная сводка: сколько сервер ПРИНЯЛ, сколько ОТВЕРГ и почему.
 * «Ответ 200» сам по себе успехом не считается: стандарт разрешает частичный
 * отказ внутри успешного ответа (FailedSOPSequence), и молчать о нём нельзя —
 * врач решит, что снимки в архиве, а их там нет.
 */
export function сводкаSTOW(ответ) {
  const принято = ответ?.['00081199']?.Value?.length || 0;
  const отказы = ответ?.['00081198']?.Value || [];
  const причины = new Map();
  for (const о of отказы) {
    const код = _знач(о, '00081197');
    const ключ = код == null ? 'без кода' : `код ${код}`;
    причины.set(ключ, (причины.get(ключ) || 0) + 1);
  }
  return {
    принято,
    отвергнуто: отказы.length,
    причины: [...причины.entries()].map(([код, сколько]) => `${код} × ${сколько}`),
    всёПринято: отказы.length === 0 && принято > 0,
  };
}

/** Подпись исследования для списка: «17.01.2024 · CT · 512 кадров · описание». */
export function подписьПрошлого(ст) {
  const д = ст.дата && ст.дата.length === 8 ? `${ст.дата.slice(6, 8)}.${ст.дата.slice(4, 6)}.${ст.дата.slice(0, 4)}` : (ст.дата || 'дата неизвестна');
  const части = [д];
  if (ст.модальности?.length) части.push(ст.модальности.join('+'));
  if (ст.кадров) части.push(`${ст.кадров} кадр.`);
  if (ст.описание) части.push(ст.описание);
  return части.join(' · ');
}


// ══════════════════════════════════════════════════════════════
//  [12.0.5] Рабочий список отделения — замена MWL, которого в браузере
//  не бывает (DIMSE C-FIND требует сырых сокетов). Список строится QIDO-
//  запросом «исследования за день/диапазон, модальность, имя» — это не
//  расписание из RIS, а фактически ПРИНЯТОЕ в архив; окно говорит об
//  этом прямо.
// ══════════════════════════════════════════════════════════════

/** ГГГГММДД для «сегодня минус n дней» (n=0 — сегодня). */
export function датаДень(nНазад = 0, теперь = new Date()) {
  const д = new Date(теперь.getTime() - nНазад * 86400000);
  const p = (x) => String(x).padStart(2, '0');
  return `${д.getFullYear()}${p(д.getMonth() + 1)}${p(д.getDate())}`;
}

/**
 * Параметры QIDO для рабочего списка. Кривой ввод — отказ, а не тихий запрос
 * «всего архива»: такой запрос на большом PACS подвесит и сервер, и вкладку.
 * @param {object} ф — { деньОт: 'ГГГГММДД', деньДо?: 'ГГГГММДД' (пусто = один
 *   день), модальность?: 'CT'|'MR'|…, пациент?: подстрока имени или ID }
 */
export function параметрыСписка(ф = {}) {
  const дата8 = (х) => /^\d{8}$/.test(String(х || ''));
  if (!дата8(ф.деньОт)) return { ok: false, причина: `дата «${ф.деньОт ?? ''}» — нужно восемь цифр ГГГГММДД` };
  if (ф.деньДо && !дата8(ф.деньДо)) return { ok: false, причина: `дата «${ф.деньДо}» — нужно восемь цифр ГГГГММДД` };
  if (ф.деньДо && String(ф.деньДо) < String(ф.деньОт)) return { ok: false, причина: 'конец диапазона раньше начала' };
  const параметры = { StudyDate: ф.деньДо && ф.деньДо !== ф.деньОт ? `${ф.деньОт}-${ф.деньДо}` : String(ф.деньОт) };
  if (ф.модальность && ф.модальность !== 'ВСЕ') параметры.ModalitiesInStudy = String(ф.модальность).toUpperCase();
  const п = String(ф.пациент || '').trim();
  if (п) {
    // Цифры с дефисами читаем как ID, буквы — как имя; подстроку оборачиваем
    // звёздочками сами: врач ищет «иван», а не выучивает синтаксис QIDO.
    if (/^[\d.\-]+$/.test(п)) параметры.PatientID = п;
    else параметры.PatientName = `*${п}*`;
  }
  return { ok: true, параметры };
}

/** QIDO /studies → строки рабочего списка, по времени (ранние сверху). */
export function разобратьСписокДня(ответQIDO) {
  const строки = [];
  for (const з of Array.isArray(ответQIDO) ? ответQIDO : []) {
    const uid = _строка(з, '0020000D');
    if (!uid) continue;
    const времяСырое = _строка(з, '00080030');
    const м = времяСырое.match(/^(\d{2})(\d{2})/);
    строки.push({
      studyUID: uid,
      дата: _строка(з, '00080020').replace(/\D/g, '').slice(0, 8),
      время: м ? `${м[1]}:${м[2]}` : '',
      времяКлюч: времяСырое || '000000',
      пациент: _строка(з, '00100010'),
      patientId: _строка(з, '00100020'),
      описание: _строка(з, '00081030'),
      модальности: _список(з, '00080061'),
      кадров: _число(з, '00201208'),
    });
  }
  строки.sort((а, б) => (а.дата + а.времяКлюч).localeCompare(б.дата + б.времяКлюч));
  return строки;
}
